import { createSelector } from '@reduxjs/toolkit';
import { getTicApiSlice } from './api-slice/tic.api.selector';
import { ticApi } from './api-slice/tic.apiSlice';
import { getContextualTicAccounts } from './tic.auth.selectors';

/**
 * Check if any connection (account or personal) has reference_tasks enabled.
 * Used to determine if task picker should be shown in HourEditor (TT-4242).
 *
 * Returns false if:
 * - No accountId available
 * - Integrations data not loaded
 * - No connections have reference_tasks: true
 */
export const getHasAnyConnectionWithReferenceTasks = createSelector(
  [getTicApiSlice, getContextualTicAccounts],
  (apiState, { workspace, personalSpace }): boolean => {
    const accountId = workspace?.id || personalSpace?.id;
    if (!accountId) return false;

    const integrationsResult = ticApi.endpoints.getIntegrations.select({ accountId })({
      [ticApi.reducerPath]: apiState,
    });

    const integrations = integrationsResult.data?.data || [];

    return integrations.some(integration => {
      const accountConnection = integration.account?.connection;
      const personalConnection = integration.personal?.connection;

      return (
        accountConnection?.settings?.reference_tasks === true ||
        personalConnection?.settings?.reference_tasks === true
      );
    });
  }
);

/**
 * Get list of connected TIC integrations that have reference_tasks enabled.
 * Used for filtering hours by linked work items (TT-4385).
 *
 * Returns empty array if:
 * - No accountId available
 * - Integrations data not loaded
 * - No connections have reference_tasks: true
 *
 * Maps TIC provider IDs to legacy app IDs:
 * - 'asana' -> 'asana_sync'
 */
export const getConnectedTicIntegrationsWithReferenceTasks = createSelector(
  [getTicApiSlice, getContextualTicAccounts],
  (apiState, { workspace, personalSpace }): Array<string> => {
    const accountId = workspace?.id || personalSpace?.id;
    if (!accountId) return [];

    const integrationsResult = ticApi.endpoints.getIntegrations.select({ accountId })({
      [ticApi.reducerPath]: apiState,
    });

    const integrations = integrationsResult.data?.data || [];

    const connectedIntegrations = integrations
      .filter(integration => {
        const accountConnection = integration.account?.connection;
        const personalConnection = integration.personal?.connection;

        return (
          accountConnection?.settings?.reference_tasks === true ||
          personalConnection?.settings?.reference_tasks === true
        );
      })
      .map(integration => integration.id);

    return connectedIntegrations;
  }
);

/**
 * Get icon URLs for TIC integrations that have reference_tasks enabled.
 * Returns a map of integration ID -> icon URL.
 *
 * Used to display provider logos in the linked work filter dropdown.
 */
export const getTicIntegrationIcons = createSelector(
  [getTicApiSlice, getContextualTicAccounts],
  (apiState, { workspace, personalSpace }): Record<string, string | null> => {
    const accountId = workspace?.id || personalSpace?.id;
    if (!accountId) return {};

    const integrationsResult = ticApi.endpoints.getIntegrations.select({ accountId })({
      [ticApi.reducerPath]: apiState,
    });

    const integrations = integrationsResult.data?.data || [];

    const iconMap: Record<string, string | null> = {};

    integrations.forEach(integration => {
      const accountConnection = integration.account?.connection;
      const personalConnection = integration.personal?.connection;

      if (
        accountConnection?.settings?.reference_tasks === true ||
        personalConnection?.settings?.reference_tasks === true
      ) {
        iconMap[integration.id] = integration.icon_url;
      }
    });

    return iconMap;
  }
);
